import { useSelector } from '@xstate/react'
import React from 'react'
import { IoCloseSharp } from 'react-icons/io5'
import styled from 'styled-components'
import { CartFieldsFragment } from 'types/graphql'
import { assertExists } from 'utils/assert'
import { numberToCurrency } from 'utils/numberToCurrency'
import { Text } from 'lib/Text'
import { useCartContext } from 'modules/cart/context/CartProvider'
import { FormStateTags } from 'modules/cart/context/cartMachine'

const CartPopoverItemContainer = styled.div`
  display: grid;
  grid-column-gap: 8px;
  grid-template-columns: 64px 1fr auto;
  grid-template-areas: 'image productInfo removeButton' 'price price price';
  width: 100%;
  padding-top: 8px;
`

const CartPopoverImage = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
  border: 1px solid var(--neutral-20);
  grid-area: image;
`

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  grid-area: productInfo;
`

const RemoveFromCartButton = styled.button`
  grid-area: removeButton;
  align-self: flex-start;
  border: none;
  background: none;
  width: 32px;
  height: 32px;
  padding: 0;
  position: relative;
  cursor: pointer;

  svg {
    top: 0;
    right: 0;
    position: absoulute;
  }

  path {
    fill: var(--neutral-60);
  }

  &:hover path {
    fill: var(--neutral-100);
  }
`

const PriceRow = styled(Text)`
  grid-area: price;
  text-align: end;
`

interface Props {
  sku: CartFieldsFragment['bags'][0]['skus'][0]
  currency: string
  fallbackThumbnail?: string
}

export const CartPopoverItem = ({ sku, currency, fallbackThumbnail }: Props) => {
  const { removeVariantFromCart, cartService } = useCartContext()
  const { productVariant } = sku
  const isBlocked = useSelector(cartService, (state) => state.hasTag(FormStateTags.blocked))
  assertExists(productVariant)

  return (
    <CartPopoverItemContainer>
      <CartPopoverImage src={sku.thumbnail ?? fallbackThumbnail} alt="" />
      <ProductInfo>
        <>
          <Text weight="medium" size="ps">
            {productVariant.product.name}
          </Text>
          <Text size="ps" color="neutral-60">
            {sku.name}
          </Text>
        </>
      </ProductInfo>
      <RemoveFromCartButton onClick={() => removeVariantFromCart(productVariant.id)} disabled={isBlocked}>
        <IoCloseSharp size={20} />
      </RemoveFromCartButton>
      <PriceRow weight="medium" size="ps">
        {sku.line_price && numberToCurrency(currency, sku.line_price)}
      </PriceRow>
    </CartPopoverItemContainer>
  )
}
